import { PromotionColor } from "./models";

// ============================================================
// Custom Template Constants (for ChatBased Embedded)
// ============================================================

/**
 * Wrapper template - fixed layout, v3.ts controls item loop
 * {{cards}} placeholder is replaced with rendered card HTML
 */
export const DEFAULT_WRAPPER_TEMPLATE = `
<div class="custom-pricing-wrapper" style="
  display: flex;
  gap: 20px;
  padding: 40px;
  justify-content: center;
  flex-wrap: wrap;
">
  {{cards}}
</div>
`;

/**
 * Card template - AI can modify this
 * Uses placeholders that v3.ts replaces with actual item data
 *
 * Available placeholders:
 * - {{index}}, {{item_id}}, {{name}}, {{description}}
 * - {{price}}, {{price_raw}}, {{currency}}, {{billing_period}}
 * - {{quantity}}, {{unit}}, {{cta_text}}
 * - {{bullet_points}}, {{focus_badge}}
 * - {{color_primary}}, {{color_secondary}}, {{color_background}}, {{color_font}}
 */
export const DEFAULT_CARD_TEMPLATE = `
<div class="custom-pricing-card" data-item-index="{{index}}" data-item-id="{{item_id}}" style="
  background: white;
  border-radius: 16px;
  padding: 32px;
  flex: 1;
  min-width: 280px;
  max-width: 340px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  position: relative;
">
  <span style="position: absolute; top: -14px; left: 50%; transform: translateX(-50%); background: {{color_primary}}; color: white; padding: 4px 16px; border-radius: 999px; font-size: 14px; font-weight: 600; white-space: nowrap; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">{{focus_badge}}</span>

  <h3 style="font-size: 24px; margin: 0 0 8px; color: {{color_font}};">
    {{name}}
  </h3>

  <p style="color: #666; margin: 0 0 16px; flex-grow: 0;">
    {{description}}
  </p>

  <div style="font-size: 36px; font-weight: bold; margin: 24px 0; color: {{color_font}};">
    {{price}}
    <span style="font-size: 16px; font-weight: normal; color: #666;">{{billing_period}}</span>
  </div>

  <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 16px; padding: 8px 12px; background: {{color_primary}}15; border-radius: 8px;">
    <span style="font-size: 20px; font-weight: 700; color: {{color_primary}};">{{quantity}}</span>
    <span style="font-size: 14px; color: #666;">{{unit}}</span>
  </div>

  <button data-action="cta" data-item-id="{{item_id}}" style="
    width: 100%;
    padding: 16px;
    background: {{color_primary}};
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    margin-top: auto;
  ">
    {{cta_text}}
  </button>

  <ul style="margin-top: 24px; padding-left: 20px; list-style: none;">
    {{bullet_points}}
  </ul>
</div>
`;

export interface BrandColorSet {
  id: string;
  primary: string;
  secondary: string;
  text: {
    primary: string;
    secondary: string;
    light: string;
  };
  background: {
    primary: string;
    secondary: string;
    accent: string;
  };
  success: string;
  danger: string;
}

// Fallback palette used when a promotion does not provide custom colors.
export const DEFAULT_PROMOTION_COLOR: PromotionColor = {
  primary: "#4ea699",
  secondary: "#140d4f",
  font_color: "#1f2937",
  font_color_secondary: "#FFFFFF",
  background_color: "#ffffff",
};

// Deprecated brand color set shape retained for backward compatibility.
export const DEFAULT_BRAND_COLOR_SETS: BrandColorSet[] = [
  {
    id: "default",
    primary: DEFAULT_PROMOTION_COLOR.primary,
    secondary: DEFAULT_PROMOTION_COLOR.secondary,
    text: {
      primary: DEFAULT_PROMOTION_COLOR.font_color,
      secondary: DEFAULT_PROMOTION_COLOR.font_color,
      light: DEFAULT_PROMOTION_COLOR.font_color,
    },
    background: {
      primary: DEFAULT_PROMOTION_COLOR.background_color,
      secondary: DEFAULT_PROMOTION_COLOR.background_color,
      accent: DEFAULT_PROMOTION_COLOR.background_color,
    },
    success: DEFAULT_PROMOTION_COLOR.primary,
    danger: DEFAULT_PROMOTION_COLOR.primary,
  },
];
